import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Menu, X, Instagram } from "lucide-react";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";
import LanguageSwitcher, { spath } from "./LanguageSwitcher";

const INSTAGRAM_URL = "https://www.instagram.com/sifirbirtuning";
const menuLabel = (title) => (title || "").replace(/^KKTC\s+/i, "");

export default function Header() {
  const { lang } = useParams();
  const [open, setOpen] = useState(false);
  const { data: nav } = useQuery({
    queryKey: ["nav", lang],
    queryFn: async () => (await api.get(`/public/nav/${lang}`)).data,
  });

  const services = nav?.services || [];
  const branches = nav?.branches || [];

  return (
    <header className="sticky top-0 z-40 bg-obsidian/85 backdrop-blur-xl border-b border-white/10">
      <div className="container-x flex items-center justify-between h-16 sm:h-20">
        <Link to={`/${lang}`} data-testid="header-logo" className="flex items-center gap-2">
          <img src="/logo.jpg" alt="Sıfır Bir Tuning" className="h-11 sm:h-14 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
          <NavDropdown label={t(lang, "services")} items={services} testid="nav-services" />
          <NavDropdown label={t(lang, "branches")} items={branches} testid="nav-branches" />
          {nav?.blog?.available && (
            <Link to={`/${lang}/blog`} data-testid="nav-blog" className="text-sm uppercase tracking-wide text-ink/90 hover:text-copper transition-colors">
              {t(lang, "blog")}
            </Link>
          )}
          <a href="#quote" data-testid="nav-contact" className="text-sm uppercase tracking-wide text-ink/90 hover:text-copper transition-colors">
            {t(lang, "contact")}
          </a>
        </nav>

        <div className="flex items-center gap-3 sm:gap-4">
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-instagram"
            aria-label="Instagram"
            className="text-ink/90 hover:text-copper transition-colors"
          >
            <Instagram size={20} />
          </a>
          <LanguageSwitcher lang={lang} />
          <a href="#quote" data-testid="header-cta" className="btn-copper hidden sm:inline-flex !py-2 !px-5">
            {t(lang, "getQuote")}
          </a>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-ink" onClick={() => setOpen((o) => !o)}
            aria-label={t(lang, "menu")}
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-white/10 bg-graphite">
          <div className="container-x py-4 space-y-4">
            <MobileGroup label={t(lang, "services")} items={services} lang={lang} onClick={() => setOpen(false)} />
            <MobileGroup label={t(lang, "branches")} items={branches} lang={lang} onClick={() => setOpen(false)} />
            {nav?.blog?.available && (
              <Link to={`/${lang}/blog`} data-testid="nav-blog-mobile" onClick={() => setOpen(false)} className="block eyebrow !text-ink hover:!text-copper">{t(lang, "blog")}</Link>
            )}
            <a href="#quote" data-testid="nav-contact-mobile" onClick={() => setOpen(false)} className="block eyebrow !text-ink hover:!text-copper">{t(lang, "contact")}</a>
            <a href="#quote" onClick={() => setOpen(false)} className="btn-copper w-full">{t(lang, "getQuote")}</a>
          </div>
        </div>
      )}
    </header>
  );
}

function NavDropdown({ label, items, testid }) {
  return (
    <div className="relative group">
      <button data-testid={testid} className="text-sm uppercase tracking-wide text-ink/90 hover:text-copper transition-colors">
        {label}
      </button>
      <div className="absolute left-0 rtl:left-auto rtl:right-0 top-full pt-3 hidden group-hover:block">
        <ul className="w-64 bg-graphite border border-white/10 rounded-lg shadow-xl overflow-hidden max-h-[70vh] overflow-y-auto">
          {items.map((it) => (
            <li key={it.entity_id}>
              <Link
                to={spath(it.path.split("/")[1], it.slug)}
                data-testid={`nav-item-${it.entity_id}`}
                className="block px-4 py-2.5 text-sm text-ink hover:bg-surface hover:text-copper transition-colors"
              >
                {menuLabel(it.title)}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function MobileGroup({ label, items, lang, onClick }) {
  return (
    <div>
      <p className="eyebrow mb-2">{label}</p>
      <ul className="space-y-1">
        {items.map((it) => (
          <li key={it.entity_id}>
            <Link to={spath(lang, it.slug)} onClick={onClick} className="block py-1.5 text-ink/90 hover:text-copper">
              {menuLabel(it.title)}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
